/**
 * Hooks do inbox de notificações do Portal do Parceiro (E21.1.5).
 *
 * D3 (passo 19): o portal tem socket (`PortalAoVivo`); o polling de ~30 s é a reserva
 * e pausa com o socket vivo. markRead/markAllRead invalidam lista + contador.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { intervaloDeReserva, usePortalSocketVivo } from "@/hooks/usePortalSocketVivo";
import { portalNotificationsApi, type PortalNotificationListResponse } from "@/services/portalApi";

export const LIST_KEY = ["portal", "notifications", "list"];
export const COUNT_KEY = ["portal", "notifications", "unread-count"];

/** Refaz a lista (todas as variantes: o sino e o Início) e a contagem — o `notification:new` e o `badge_update` de leitura do socket do portal. */
export function invalidarNotificacoesDoPortal(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: LIST_KEY });
  void qc.invalidateQueries({ queryKey: COUNT_KEY });
}

/** Tamanho de página do feed infinito do Início (I1). O backend capa `limit`
 * em 50 (`Query(..., le=50)`) — por isso a paginação SOBE POR `offset`, nunca
 * aumentando `limit`: o round 1 pedia `limit=20`, depois `40`, depois `60` —
 * e `60` virava 422 na terceira página. Aqui `limit` fica travado em
 * `TAMANHO_DA_PAGINA_INFINITA` para sempre; só `offset` cresce. */
const TAMANHO_DA_PAGINA_INFINITA = 20;

export function usePortalNotificationsList(only_unread = false, limit = 20) {
  const socketVivo = usePortalSocketVivo();
  return useQuery({
    queryKey: [...LIST_KEY, only_unread, limit],
    queryFn: () => portalNotificationsApi.list({ limit, only_unread }),
    staleTime: 15000,
    refetchInterval: intervaloDeReserva(socketVivo), // reserva: pausa com o socket vivo (D3)
  });
}

/**
 * I1 — feed de notificações do Início, paginado de verdade por `offset`
 * (`useInfiniteQuery`, 20 por página). `getNextPageParam` decide pelo
 * `has_more` do backend (não por `items.length < pageSize`: a última página
 * pode vir cheia). A chave estende `LIST_KEY` de propósito — o MESMO prefixo
 * que `usePortalNotificationsList` (o sino) usa: `useMarkPortalNotificationRead`
 * e `useMarkAllPortalNotificationsRead` já invalidam `LIST_KEY` inteiro, então
 * marcar como lida em QUALQUER lugar (sino ou Início) invalida os dois sem
 * precisar tocar nesses hooks. Ao invalidar, o React Query refaz TODAS as
 * páginas já carregadas desta query — a lista não encolhe de volta pra 1
 * página.
 *
 * Continua com o MESMO `refetchInterval`/`staleTime` do sino — é uma query
 * DIFERENTE (chave própria, com o sufixo "infinito"), então o sino (limit 20
 * fixo) não quebra e continua pollando sozinho.
 */
export function usePortalNotificationsInfinite() {
  const socketVivo = usePortalSocketVivo();
  return useInfiniteQuery({
    queryKey: [...LIST_KEY, "infinito"],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      portalNotificationsApi.list({ limit: TAMANHO_DA_PAGINA_INFINITA, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (ultimaPagina: PortalNotificationListResponse, todasAsPaginas) =>
      ultimaPagina.has_more ? todasAsPaginas.length * TAMANHO_DA_PAGINA_INFINITA : undefined,
    staleTime: 15000,
    refetchInterval: intervaloDeReserva(socketVivo), // reserva: pausa com o socket vivo (D3)
  });
}

export function usePortalUnreadCount() {
  const socketVivo = usePortalSocketVivo();
  return useQuery({
    queryKey: COUNT_KEY,
    queryFn: () => portalNotificationsApi.unreadCount(),
    refetchInterval: intervaloDeReserva(socketVivo), // reserva: pausa com o socket vivo (D3)
    staleTime: 15000,
  });
}

export function useMarkPortalNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => portalNotificationsApi.markRead(id),
    onSuccess: () => invalidarNotificacoesDoPortal(qc),
  });
}

export function useMarkAllPortalNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalNotificationsApi.markAllRead(),
    onSuccess: () => invalidarNotificacoesDoPortal(qc),
  });
}
